import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { ChatMessage, ProChatInstance } from '@ant-design/pro-chat';
import mean from 'lodash/mean';
import get from 'lodash/get';
import { RootState } from '../app/store';
import { useSelector } from 'react-redux';
import { useAgentDispatch } from './useAgentStore';
import { BeErrorCodeUnusableOrgCredential } from '../enums/beErrorCode';
import {
  cancelThreadRun,
  handleThreadRequest,
  setRevokeAPICall,
  ChatMessageResult,
  getCopilotAssistantMessages,
  POLL_THREAD_RUN_INTERVAL_MS,
} from '../services/copilotThread.service';
import { setResult } from '../app/Slices/adminAgentSlice';
import {
  ContentObjectType,
  CopilotChatMessageResponse,
  ThreadRun,
  ThreadRunStatus,
} from '../interfaces/copilotChat';
import { sleep } from '../utils/sleep';
import {
  CopilotChatMessageStatus,
  CopilotChatRole,
} from '../enums/copilotChat';
import DotLoader from '../components/Loaders/DotsLoader';

const TRANSIENT_ERROR_CODE_SF_OAUTH_EXPIRED = 499;
const RENDERING_ACCELERATION_FACTOR = 4;

export type HandleRequestFunction = (
  chatMessages: ChatMessage[],
  onMsgUpdate?: (msgsResult: ChatMessageResult) => void
) => void;

export type UseCopilotChatProps = {
  assistantType: { assistant_type: string };
  threadRun: { context?: string };
  filterMessages?: (
    msgs: CopilotChatMessageResponse[]
  ) => CopilotChatMessageResponse[];
  setLoadingCallback?: (state: boolean) => void;
  handleRequestCallback?: (message: string) => void;
};
export const useCopilotChat = ({
  assistantType,
  threadRun,
  filterMessages,
  setLoadingCallback,
  handleRequestCallback,
}: UseCopilotChatProps) => {
  const chatRef = useRef<ProChatInstance>();
  const [currentThreadRun, setCurrentThreadRun] = useState<null | ThreadRun>(
    null
  );
  const [currentMessage, setCurrentMessage] = useState<string>('');
  const [isSending, setIsSending] = useState<boolean>(false);
  const dispatchAgent = useAgentDispatch();
  const org = useSelector((state: RootState) => state.organization.org);
  const loaderRootRef = useRef<Root | null>(null); // Store the React root for loader
  const loaderContainerRef = useRef<HTMLElement | null>(null);
  const cleanupTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const _renderLoader = (
    loaderContainer: HTMLElement,
    contentNode: React.ReactNode
  ) => {
    // Clear any pending cleanup
    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }

    setTimeout(() => {
      if (
        loaderRootRef.current &&
        loaderContainerRef.current === loaderContainer
      ) {
        loaderRootRef.current.render(contentNode);
      } else {
        // Properly cleanup previous root before creating new one
        if (loaderRootRef.current) {
          try {
            loaderRootRef.current.unmount();
          } catch (error) {
            console.warn('Error unmounting previous loader root:', error);
          }
          loaderRootRef.current = null;
          loaderContainerRef.current = null;
        }

        try {
          const root = createRoot(loaderContainer);
          loaderRootRef.current = root;
          loaderContainerRef.current = loaderContainer;
          root.render(contentNode);
        } catch (error) {
          console.warn('Error creating new loader root:', error);
        }
      }
    }, 0);
  };

  // Show the loader if the thread run is in progress
  useLayoutEffect(() => {
    const observer = new MutationObserver(() => {
      const loaderParent = document.querySelector(
        '.ant-pro-chat-chat-list-container'
      );
      const hasChatLoaded =
        loaderParent?.children && loaderParent.children.length >= 2;
      const loaderContainer = hasChatLoaded
        ? loaderParent?.lastElementChild
        : null;

      const isRunInProgress =
        isSending || currentThreadRun?.status === ThreadRunStatus.IN_PROGRESS;

      if (loaderContainer) {
        const loaderContent = isRunInProgress ? <DotLoader /> : <div />;
        _renderLoader(loaderContainer as HTMLElement, loaderContent);
      }

      if (loaderRootRef.current) {
        // Defer disconnect to avoid unmount-in-render error
        queueMicrotask(() => observer.disconnect());
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();

      // Clear any pending timeouts
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
        cleanupTimeoutRef.current = null;
      }

      // Schedule cleanup with a small delay to avoid React render collision
      cleanupTimeoutRef.current = setTimeout(() => {
        if (loaderRootRef.current) {
          try {
            loaderRootRef.current.unmount();
          } catch (error) {
            console.warn('Error unmounting loader root on cleanup:', error);
          }
          loaderRootRef.current = null;
          loaderContainerRef.current = null;
        }
        cleanupTimeoutRef.current = null;
      }, 100);
    };
  }, [currentThreadRun?.status, isSending]);

  // Final cleanup on component unmount
  useEffect(() => {
    return () => {
      // Clear any pending timeouts
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
        cleanupTimeoutRef.current = null;
      }

      // Immediate cleanup on unmount
      if (loaderRootRef.current) {
        try {
          loaderRootRef.current.unmount();
        } catch (error) {
          console.warn(
            'Error unmounting loader root on component unmount:',
            error
          );
        }
        loaderRootRef.current = null;
        loaderContainerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const handleClick = (e: Event) => {
      const path = e.composedPath();
      const container = document.getElementById('copilot');

      if (!container || !path.includes(container)) {
        return;
      }

      const target = e.target as HTMLElement;
      const anchor = target.closest('a');

      if (anchor) {
        e.preventDefault();
        window.open(anchor.href, '_blank');
      }
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, []);

  /*
    Calculate the render speed params, so that the appended message can be rendered in `limitTimeMs`
    @param lastMsg - The message object. Render speed depends on the status of the message
    @param appendedLength - The length of the message chunk to display
    @param renderConsumptionMs - The (assumsed) time it takes to render the ProChat message once
    @param limitTimeMs - The message chunk is expected to be rendered in this time
    @returns The render speed params
  */
  const _calculateRenderSpeedParams = ({
    lastMsg,
    appendedLength,
    renderConsumptionMs,
    limitTimeMs,
  }: {
    lastMsg: CopilotChatMessageResponse;
    appendedLength: number;
    renderConsumptionMs: number;
    limitTimeMs: number;
  }) => {
    let _delayMs = 0;
    let _charStep = 1;
    if (lastMsg.status === CopilotChatMessageStatus.IN_PROGRESS) {
      _delayMs = Math.round(limitTimeMs / appendedLength);
      _charStep = Math.round(
        (renderConsumptionMs * appendedLength) / limitTimeMs
      );
    } else {
      // Render the whole message chunk as fast as possible
      _delayMs = 0;
      _charStep = appendedLength;
    }
    _delayMs = Math.ceil(_delayMs / RENDERING_ACCELERATION_FACTOR); // Speed up the rendering by 8x
    _charStep = Math.max(1, _charStep);
    return [_delayMs, _charStep];
  };

  const _streamMessage = async ({
    msgs,
    lastMsg,
    lastStreamIndex,
    streamingIntervalMs,
    onMsgUpdate,
  }: {
    msgs: CopilotChatMessageResponse[];
    lastMsg: CopilotChatMessageResponse;
    lastStreamIndex: number;
    streamingIntervalMs: number;
    onMsgUpdate: (
      msgsResult: ChatMessageResult,
      msg: CopilotChatMessageResponse
    ) => void;
  }) => {
    let _lastStreamIndex = lastStreamIndex;
    const lastMsgContent = lastMsg.content as string;

    const appendedLength = lastMsgContent.length - _lastStreamIndex;

    if (appendedLength < 0) {
      console.warn(
        `Invalid lastStreamIndex: ${_lastStreamIndex}. Message length is ${lastMsgContent.length}`
      );
      return { lastStreamIndex: lastMsgContent.length };
    } else if (appendedLength === 0) {
      console.log(
        `Nothing to append: lastStreamIndex is the same as total chars: ${lastMsgContent.length}`
      );
      return { lastStreamIndex: _lastStreamIndex };
    }

    const calRenderSpeed = ({
      renderConsumptionMs,
      limitTimeMs,
    }: {
      renderConsumptionMs: number;
      limitTimeMs: number;
    }) => {
      const [_delayMs, _charStep] = _calculateRenderSpeedParams({
        lastMsg,
        appendedLength,
        renderConsumptionMs,
        limitTimeMs,
      });
      return [_delayMs, _charStep];
    };

    // Initialize the render speed
    let [charDelayMs, charStep] = calRenderSpeed({
      renderConsumptionMs: 100,
      limitTimeMs: streamingIntervalMs,
    });
    if (charStep <= 0) {
      console.warn(
        `Invalid charStep: ${charStep}. Message length is ${lastMsgContent.length}`
      );
      return { lastStreamIndex: _lastStreamIndex };
    }
    console.log(
      `Total chars: ${lastMsgContent.length}. Displaying ${appendedLength} new chars starting at index ${_lastStreamIndex} with charDelayMs: ${charDelayMs} and charStep: ${charStep}`
    );
    // Stream the newly appended content in streamingIntervalMs seconds
    const times = [];
    for (
      let i = _lastStreamIndex + charStep;
      i < lastMsgContent.length + charStep;
      i += charStep
    ) {
      const timeStart = new Date();
      const latestContentStr = lastMsgContent.slice(0, i);
      _lastStreamIndex = latestContentStr.length;
      const msg = {
        ...lastMsg,
        content: latestContentStr,
      };
      const latestResult = getCopilotAssistantMessages([
        ...msgs.slice(0, msgs.length - 1),
        msg,
      ]);
      onMsgUpdate(latestResult, msg);
      await sleep(charDelayMs);

      // Adjust the render speed
      const timeEnd = new Date();
      times.push(timeEnd.getTime() - timeStart.getTime());
      const avgRenderTimeMs = mean(times);
      [charDelayMs, charStep] = calRenderSpeed({
        renderConsumptionMs: avgRenderTimeMs,
        limitTimeMs: streamingIntervalMs,
      });
    }

    return { lastStreamIndex: _lastStreamIndex };
  };

  const _isProChatDefaultMsgId = (id: string) => {
    return id.length < 10;
  };

  const handleRequest = async (chatMessages: ChatMessage[]) => {
    // If the ID of ProChat message is default Pro Chat ID -> update it to Cirra message ID
    const correctProChatMsgId = ({
      proChatRef,
      proChatMsg,
      msgsResult,
    }: {
      proChatRef: React.RefObject<ProChatInstance | undefined>;
      proChatMsg: ChatMessage;
      msgsResult: ChatMessageResult;
    }) => {
      if (proChatRef.current) {
        const proChatId = proChatMsg.id || '';
        const cirraMsgId = msgsResult.chat?.id || '';
        if (_isProChatDefaultMsgId(proChatId)) {
          proChatRef.current.setMessageValue(proChatId, 'id', cirraMsgId);
        }
        return cirraMsgId;
      }
      return proChatMsg.id;
    };

    const onMsgUpdate = (
      _msgsResult: ChatMessageResult,
      _msg: CopilotChatMessageResponse
    ) => {
      const msgsResult = _msgsResult as ChatMessageResult;
      if (msgsResult.result && msgsResult.result.length > 0) {
        dispatchAgent(setResult(msgsResult.result));
      }

      if (msgsResult.chat && chatRef.current) {
        const allChatMsgs = chatRef.current.getChatMessages().reverse();
        let lastAssistantChatMsg = allChatMsgs.find(
          (m) => m.role === CopilotChatRole.ASSISTANT
        );

        if (lastAssistantChatMsg) {
          if (
            !_isProChatDefaultMsgId(lastAssistantChatMsg.id) &&
            msgsResult.chat?.id !== lastAssistantChatMsg.id &&
            chatRef.current
          ) {
            const newProChatId = msgsResult.chat?.id;
            chatRef.current.pushChat({
              id: newProChatId,
              content: '...',
              role: CopilotChatRole.ASSISTANT,
              extra: {
                isFollowUp: allChatMsgs[0].role === CopilotChatRole.ASSISTANT,
                isFollowUp1: true,
              },
            });
            const newProChat = chatRef.current.getChatById(newProChatId);
            lastAssistantChatMsg = newProChat;
          }

          const targetChatMsg = lastAssistantChatMsg;
          const proChatMsgId = correctProChatMsgId({
            proChatRef: chatRef,
            proChatMsg: targetChatMsg,
            msgsResult,
          });

          // attach the original message to use later
          chatRef.current.setMessageValue(proChatMsgId, 'extra', {
            ...(lastAssistantChatMsg.extra || {}),
            originData: _msg,
          });

          const msgContentStrOrObj = msgsResult.chat?.content as string;
          if (typeof msgContentStrOrObj === 'string') {
            chatRef.current.setMessageContent(proChatMsgId, msgContentStrOrObj);
          } else {
            chatRef.current.setMessageContent(
              proChatMsgId,
              JSON.stringify([msgContentStrOrObj])
            );
          }
        }
      }
    };

    const userMessage = chatMessages[chatMessages.length - 1].content as string;

    let lastMsgId: string;
    let lastMsgContentIndex = 0;

    const onFetchMsgs = async (
      msgs: CopilotChatMessageResponse[],
      run: ThreadRun
    ) => {
      setCurrentThreadRun(run);
      const lastMsg = msgs[msgs.length - 1];
      if (!lastMsg || lastMsg.role !== CopilotChatRole.ASSISTANT) {
        return;
      }
      const hasMsgCompleted =
        lastMsg.status === CopilotChatMessageStatus.COMPLETED;
      if (handleRequestCallback && hasMsgCompleted) {
        handleRequestCallback(lastMsg.content as string);
      }

      const isMsgStreamable = typeof lastMsg.content === 'string';
      if (isMsgStreamable) {
        if (lastMsg.id !== lastMsgId) {
          lastMsgId = lastMsg.id;
          lastMsgContentIndex = 0;
        }
        const streamingResult = await _streamMessage({
          msgs,
          lastMsg,
          lastStreamIndex: lastMsgContentIndex,
          streamingIntervalMs: POLL_THREAD_RUN_INTERVAL_MS,
          onMsgUpdate,
        });
        lastMsgContentIndex = streamingResult.lastStreamIndex;
      } else {
        if (
          hasMsgCompleted ||
          get(lastMsg, 'content.type') === ContentObjectType.TOOL_CALL
        ) {
          const latestResult = getCopilotAssistantMessages(msgs);
          onMsgUpdate(latestResult, lastMsg);
        }
      }
    };

    try {
      await handleThreadRequest({
        threadMessage: userMessage,
        assistantType: assistantType,
        threadRun: threadRun,
        filterMsgs: filterMessages,
        onFetchMsgs,
      });
      setIsSending(false);
      // The new messege is already displayed by onFetchMsgs
      return new Response(null);
    } catch (error) {
      let statusText = 'Something went wrong';
      let statusCode = get(error, 'response.status') || 500;
      const beErrorCode = get(error, 'response.data.error_code') || '';
      if (
        (Object.values(BeErrorCodeUnusableOrgCredential) as string[]).includes(
          beErrorCode
        )
      ) {
        statusCode = TRANSIENT_ERROR_CODE_SF_OAUTH_EXPIRED;
        statusText =
          'Your Salesforce authentication is expired. Please re-authenticate.';
      } else {
        statusText = (error as Error).message;
      }
      setIsSending(false);
      return new Response(null, {
        status: statusCode,
        statusText,
      });
    }
  };

  const setLoadingState = (state: boolean) => {
    setIsSending(state);
    setLoadingCallback && setLoadingCallback(state);
  };

  const handleRevokeAPICall = async () => {
    try {
      setRevokeAPICall(true);
      cancelThreadRun();
    } catch (error) {
      console.error('Error revoking API call:', error);
    }
  };

  const handleSendMessage = async (currentMessage: string) => {
    try {
      if (isSending) {
        handleRevokeAPICall();
        chatRef.current?.stopGenerateMessage();
        setLoadingState(false);
        return;
      }
      setLoadingState(true);
      chatRef.current?.sendMessage(currentMessage);
      setCurrentMessage('');
    } catch (error) {
      const errorResponseOptions = {
        status: 500,
        statusText: 'Something went wrong',
      };
      setLoadingState(false);
      return new Response(null, errorResponseOptions);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newMessage = e.target?.value;
    setCurrentMessage(newMessage);
  };

  const onChatChange = (msgs: ChatMessage[]) => {
    /*
    if (!msgs.length) {
      return;
    }
    const lastMsg = last(msgs);
    const isOauthError =
      get(lastMsg, `error.type`) === TRANSIENT_ERROR_CODE_SF_OAUTH_EXPIRED;
    if (isOauthError) {
      const newOrg = { ...org, active: false } as OrgCredential;
      dispatch(setOrganization(newOrg));
    }
      */
  };

  return {
    chatRef,
    currentThreadRun,
    currentMessage,
    setCurrentMessage,
    isSending,
    handleRequest,
    handleSendMessage,
    handleChange,
    onChatChange,
    org,
  };
};
